import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatTimeAgo } from '../lib/utils';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, ShoppingBag, Package, Truck, CheckCircle,
  Clock, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrdersPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [buyingOrders, setBuyingOrders] = useState([]);
  const [sellingOrders, setSellingOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const [buyingRes, sellingRes] = await Promise.all([
        axios.get(`${API}/marketplace/orders/buying`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/marketplace/orders/selling`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBuyingOrders(buyingRes.data);
      setSellingOrders(sellingRes.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const markAsShipped = async (orderId) => {
    try {
      await axios.put(`${API}/marketplace/orders/${orderId}/ship`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order marked as shipped!');
      fetchOrders();
    } catch (error) {
      toast.error('Error');
    }
  };

  const confirmDelivery = async (orderId) => {
    try {
      await axios.put(`${API}/marketplace/orders/${orderId}/deliver`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Livrezon konfime!');
      fetchOrders();
    } catch (error) {
      toast.error('Error');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'shipped':
        return <Truck className="w-5 h-5 text-blue-500" />;
      case 'paid':
        return <Package className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'delivered': return 'Livre';
      case 'shipped': return 'Ekspedye';
      case 'paid': return 'Peye';
      case 'pending': return 'An atant';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const OrderCard = ({ order, isSeller }) => (
    <Card className="mb-4">
      <CardContent className="pt-4">
        <div className="flex gap-4">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-secondary/30 shrink-0">
            {order.product_image ? (
              <img src={order.product_image} alt={order.product_title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold line-clamp-1">{order.product_title}</h3>
            <p className="text-sm text-muted-foreground">
              {order.quantity}x ${order.unit_price.toFixed(2)}
            </p>
            <p className="text-lg font-bold text-primary">${order.total_price.toFixed(2)}</p>
            
            <div className="flex items-center gap-2 mt-2">
              {getStatusIcon(order.status)}
              <span className="text-sm">{getStatusText(order.status)}</span>
            </div>

            {isSeller && order.status === 'paid' && (
              <Button
                size="sm"
                className="mt-3 rounded-full"
                onClick={() => markAsShipped(order.id)}
              >
                <Truck className="w-4 h-4 mr-1" />
                Make Ekspedye
              </Button>
            )}

            {!isSeller && order.status === 'shipped' && (
              <Button
                size="sm"
                className="mt-3 rounded-full"
                onClick={() => confirmDelivery(order.id)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Konfime Livrezon
              </Button>
            )}
          </div>
        </div>
        
        {isSeller && (
          <div className="mt-4 p-3 bg-secondary/30 rounded-xl text-sm">
            <p><strong>Address:</strong> {order.shipping_address}</p>
            <p><strong>Phone:</strong> {order.phone}</p>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground mt-3">
          {formatTimeAgo(order.created_at, 'ht')}
        </p>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-2xl mx-auto pb-20" data-testid="orders-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-primary" />
            <h1 className="font-bold text-lg">Orders</h1>
          </div>
        </div>
      </header>

      <Tabs defaultValue="buying" className="w-full">
        <TabsList className="w-full justify-start border-b border-border/50 rounded-none bg-transparent h-12 p-0 px-4">
          <TabsTrigger 
            value="buying" 
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Mwen Achte ({buyingOrders.length})
          </TabsTrigger>
          <TabsTrigger 
            value="selling" 
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Mwen Vann ({sellingOrders.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="buying" className="p-4">
          {buyingOrders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Ou poko achte anyen</p>
            </div>
          ) : (
            buyingOrders.map(order => (
              <OrderCard key={order.id} order={order} isSeller={false} />
            ))
          )}
        </TabsContent>
        
        <TabsContent value="selling" className="p-4">
          {sellingOrders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Ou poko vann anyen</p>
            </div>
          ) : (
            sellingOrders.map(order => (
              <OrderCard key={order.id} order={order} isSeller={true} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
